import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInputField } from "@/shared/components/field/input-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { baseDataTextVariables } from "@/services/backend/omni/offer/types/offer-text-variables";
import {
  PatchRequest,
  updateOmniOfferBaseDataCache,
  useOmniOfferBaseDataPatchMutation,
  useOmniOfferBaseDataShowQuery,
} from "../api/base-data-api";
import { OfferBaseDataPatch, patchOfferBaseData } from "../types/base-data";

export function OfferBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading,
  } = useOmniOfferBaseDataShowQuery({ id });
  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateOmniOfferBaseDataCache,
    useOmniOfferBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }
  if (isLoading || !data) {
    return <CardContentSkeleton />;
  }

  const variables = baseDataTextVariables(data.serviceKind);
  const patch = (p: OfferBaseDataPatch) =>
    set({ ...request, ...p }, patchOfferBaseData(data, p));

  return (
    <div className="grid gap-4">
      {/* A rename to a taken label is a conflict, not a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => patch({ label })}
        fieldName="label"
      />
      <TextInputField
        label={t("Titel")}
        text={data.title}
        onTextChange={(title) => patch({ title })}
        fieldName="title"
      />
      <TextInputField
        label={t("Untertitel")}
        text={data.subtitle}
        onTextChange={(subtitle) => patch({ subtitle })}
        fieldName="subtitle"
      />
      <EditorInputField
        // The editor highlights the variables it is mounted with.
        key={variables.map((v) => v.name).join(",")}
        label={t("Text")}
        initialHtml={data.text}
        onHtmlChange={(text: string) => patch({ text })}
        variables={variables}
        fieldName="text"
      />
    </div>
  );
}
